/**
 * PaymentModal - Credit card payment form modal.
 *
 * Follows existing modal pattern (AvatarOnboardingModal):
 * fixed overlay + centered card content.
 *
 * Features:
 * - Plan summary header
 * - Card number, holder name, CPF/CNPJ, expiry, CVV fields
 * - Input masks for card number, CPF/CNPJ
 * - Inline validation per field
 * - Loading state during submission
 * - ESC key and backdrop click to close
 *
 * SECURITY: Card data stays in local state only, sent via API, never stored.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { X, Loader2 } from 'lucide-react';
import { trackFunnelEvent } from '@/services/adminDashboardService';
import type { Plan, BillingPeriod } from '@/types';

interface PaymentModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (payment: PaymentFormData) => Promise<void>;
  plan: Plan;
  period: BillingPeriod;
  loading?: boolean;
}

export interface PaymentFormData {
  cardNumber: string;
  holderName: string;
  cpfCnpj: string;
  expMonth: string;
  expYear: string;
  cvv: string;
}

interface FieldErrors {
  cardNumber?: string;
  holderName?: string;
  cpfCnpj?: string;
  expMonth?: string;
  expYear?: string;
  cvv?: string;
}

// ============================================================================
// Input Masks
// ============================================================================

function maskCardNumber(value: string): string {
  const clean = value.replace(/\D/g, '').slice(0, 16);
  return clean.replace(/(\d{4})(?=\d)/g, '$1 ');
}

function maskCpfCnpj(value: string): string {
  const clean = value.replace(/\D/g, '');
  if (clean.length <= 11) {
    // CPF: 000.000.000-00
    return clean
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }
  // CNPJ: 00.000.000/0000-00
  return clean
    .slice(0, 14)
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
}

// ============================================================================
// Validation
// ============================================================================

function validateField(name: string, value: string, t: (key: string) => string): string | undefined {
  switch (name) {
    case 'cardNumber': {
      const clean = value.replace(/\D/g, '');
      if (!clean) return t('plans.validation.cardRequired');
      if (clean.length < 13 || clean.length > 19) return t('plans.validation.cardInvalid');
      return undefined;
    }
    case 'holderName':
      if (!value.trim()) return t('plans.validation.nameRequired');
      if (value.trim().length < 3) return t('plans.validation.nameInvalid');
      return undefined;
    case 'cpfCnpj': {
      const clean = value.replace(/\D/g, '');
      if (!clean) return t('plans.validation.cpfCnpjRequired');
      if (clean.length !== 11 && clean.length !== 14) return t('plans.validation.cpfCnpjInvalid');
      return undefined;
    }
    case 'expMonth': {
      const month = parseInt(value, 10);
      if (!value || isNaN(month) || month < 1 || month > 12) return t('plans.validation.monthInvalid');
      return undefined;
    }
    case 'expYear': {
      if (!value || !/^\d{2}$/.test(value)) return t('plans.validation.yearInvalid');
      return undefined;
    }
    case 'cvv':
      if (!value || !/^\d{3,4}$/.test(value)) return t('plans.validation.cvvInvalid');
      return undefined;
    default:
      return undefined;
  }
}

// ============================================================================
// Component
// ============================================================================

const PaymentModal: React.FC<PaymentModalProps> = ({
  open,
  onClose,
  onConfirm,
  plan,
  period,
  loading = false,
}) => {
  const { t } = useTranslation();

  const [form, setForm] = useState<PaymentFormData>({
    cardNumber: '',
    holderName: '',
    cpfCnpj: '',
    expMonth: '',
    expYear: '',
    cvv: '',
  });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const formStartedRef = useRef(false);

  // Reset form on open + emit checkout_initiated
  useEffect(() => {
    if (open) {
      setForm({ cardNumber: '', holderName: '', cpfCnpj: '', expMonth: '', expYear: '', cvv: '' });
      setErrors({});
      setSubmitError(null);
      formStartedRef.current = false;
      // Fire-and-forget: user opened the payment modal (intent to pay)
      trackFunnelEvent('checkout_initiated', { plan_id: plan.planId, period }).catch(() => {});
    }
  }, [open, plan.planId, period]);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !loading) onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, loading, onClose]);

  const handleChange = useCallback((name: keyof PaymentFormData, raw: string) => {
    // Emit checkout_form_started on first interaction
    if (!formStartedRef.current && raw.length > 0) {
      formStartedRef.current = true;
      trackFunnelEvent('checkout_form_started', { first_field: name }).catch(() => {});
    }

    let value = raw;
    if (name === 'cardNumber') value = maskCardNumber(raw);
    else if (name === 'cpfCnpj') value = maskCpfCnpj(raw);
    else if (name === 'expMonth') value = raw.replace(/\D/g, '').slice(0, 2);
    else if (name === 'expYear') value = raw.replace(/\D/g, '').slice(0, 2);
    else if (name === 'cvv') value = raw.replace(/\D/g, '').slice(0, 4);

    setForm((prev) => ({ ...prev, [name]: value }));
    // Clear error on change
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  }, [errors]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    // Validate all fields
    const newErrors: FieldErrors = {};
    for (const key of Object.keys(form) as (keyof PaymentFormData)[]) {
      const err = validateField(key, form[key], t);
      if (err) newErrors[key] = err;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      // Send raw (unmasked) card number to API
      await onConfirm({
        ...form,
        cardNumber: form.cardNumber.replace(/\s/g, ''),
        cpfCnpj: form.cpfCnpj.replace(/\D/g, ''),
      });
    } catch (err: any) {
      setSubmitError(err.message || t('plans.error'));
    }
  };

  if (!open) return null;

  const formatPrice = (v: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);
  const totalPrice = period === 'MONTHLY'
    ? plan.priceMonthly
    : period === 'QUARTERLY'
      ? plan.priceQuarterly
      : plan.priceYearly;
  const divisor = period === 'YEARLY' ? 12 : period === 'QUARTERLY' ? 3 : 1;
  const displayPrice = totalPrice / divisor;
  const priceLabel = t('plans.perMonth');
  const billingNote = period === 'YEARLY'
    ? t('plans.billedAnnually').replace('{price}', formatPrice(totalPrice))
    : period === 'QUARTERLY'
      ? t('plans.billedQuarterly').replace('{price}', formatPrice(totalPrice))
      : null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-50"
        onClick={loading ? undefined : onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto bg-card">
          {/* Header */}
          <div className="flex items-center justify-between p-6 pb-0">
            <h2 className="text-xl font-bold text-foreground">
              {t('plans.payment.title')}
            </h2>
            <button
              onClick={loading ? undefined : onClose}
              className="p-1.5 rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Plan summary */}
          <div className="mx-6 mt-4 rounded-xl bg-primary p-5 text-primary-foreground text-center">
            <p className="text-xs font-medium uppercase tracking-wider opacity-80">
              {t('plans.payment.newPlan')}
            </p>
            <p className="text-xl font-bold mt-1">{plan.name}</p>
            <p className="text-lg font-semibold mt-1">
              {formatPrice(displayPrice)} <span className="text-sm font-normal opacity-80">{priceLabel}</span>
            </p>
            {billingNote && (
              <p className="text-xs opacity-70 mt-1">
                {billingNote}
              </p>
            )}
          </div>

          {/* Payment form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div className="border border-border rounded-xl p-5 space-y-4">
              <h3 className="text-base font-semibold text-foreground">
                {t('plans.payment.info')}
              </h3>

              {/* Card number */}
              <div>
                <Label htmlFor="cardNumber" className="text-sm font-medium">
                  {t('plans.payment.cardNumber')}
                </Label>
                <Input
                  id="cardNumber"
                  value={form.cardNumber}
                  onChange={(e) => handleChange('cardNumber', e.target.value)}
                  placeholder="0000 0000 0000 0000"
                  maxLength={19}
                  className="mt-1"
                  disabled={loading}
                />
                {errors.cardNumber && (
                  <p className="text-xs text-destructive mt-1">{errors.cardNumber}</p>
                )}
              </div>

              {/* Name + CPF/CNPJ */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="holderName" className="text-sm font-medium">
                    {t('plans.payment.holderName')}
                  </Label>
                  <Input
                    id="holderName"
                    value={form.holderName}
                    onChange={(e) => handleChange('holderName', e.target.value)}
                    className="mt-1"
                    disabled={loading}
                  />
                  {errors.holderName && (
                    <p className="text-xs text-destructive mt-1">{errors.holderName}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="cpfCnpj" className="text-sm font-medium">
                    {t('plans.payment.cpfCnpj')}
                  </Label>
                  <Input
                    id="cpfCnpj"
                    value={form.cpfCnpj}
                    onChange={(e) => handleChange('cpfCnpj', e.target.value)}
                    maxLength={18}
                    className="mt-1"
                    disabled={loading}
                  />
                  {errors.cpfCnpj && (
                    <p className="text-xs text-destructive mt-1">{errors.cpfCnpj}</p>
                  )}
                </div>
              </div>

              {/* Month + Year + CVV */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label htmlFor="expMonth" className="text-sm font-medium">
                    {t('plans.payment.month')}
                  </Label>
                  <Input
                    id="expMonth"
                    value={form.expMonth}
                    onChange={(e) => handleChange('expMonth', e.target.value)}
                    placeholder="MM"
                    maxLength={2}
                    className="mt-1"
                    disabled={loading}
                  />
                  {errors.expMonth && (
                    <p className="text-xs text-destructive mt-1">{errors.expMonth}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="expYear" className="text-sm font-medium">
                    {t('plans.payment.year')}
                  </Label>
                  <Input
                    id="expYear"
                    value={form.expYear}
                    onChange={(e) => handleChange('expYear', e.target.value)}
                    placeholder="AA"
                    maxLength={2}
                    className="mt-1"
                    disabled={loading}
                  />
                  {errors.expYear && (
                    <p className="text-xs text-destructive mt-1">{errors.expYear}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="cvv" className="text-sm font-medium">
                    {t('plans.payment.cvv')}
                  </Label>
                  <Input
                    id="cvv"
                    value={form.cvv}
                    onChange={(e) => handleChange('cvv', e.target.value)}
                    maxLength={4}
                    className="mt-1"
                    disabled={loading}
                  />
                  {errors.cvv && (
                    <p className="text-xs text-destructive mt-1">{errors.cvv}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Submit error */}
            {submitError && (
              <div className="p-3 rounded-lg bg-destructive/10 text-destructive border border-destructive/20 text-sm">
                {submitError}
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={loading}
              >
                {t('plans.payment.cancel')}
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    {t('plans.payment.processing')}
                  </>
                ) : (
                  t('plans.payment.confirm')
                )}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </>
  );
};

export default PaymentModal;
